import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';
import {
  ACCESS_TOKEN_STRING,
  REFRESH_TOKEN_STRING,
  CHECK_EMAIL_PATH,
} from '../constants/Constant';
import { useNavigate } from 'react-router-dom';
import { User } from '../types/AuthTypes';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import {
  setIsBackPaddockStaff,
  clearStaffState,
} from '../redux/slices/StaffSlice';
import { clearActiveOrganisation } from '../redux/slices/ActiveOrganisationSlice';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  setIsAuthenticated: () => {},
  logout: () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

const BACK_PADDOCK_DOMAIN = '@backpaddock.com.au';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  useEffect(() => {
    const checkAuth = () => {
      const token = Cookies.get(ACCESS_TOKEN_STRING);

      if (!token) {
        setUser(null);
        setIsAuthenticated(false);
        return;
      }

      try {
        const decoded = jwtDecode<{
          sub?: string;
          firstName?: string;
          lastName?: string;
          email?: string;
          role?: string[] | string;
          exp?: number;
        }>(token);

        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          console.warn('Token expired');
          logout();
          return;
        }

        setUser({
          firstName: decoded.firstName,
          lastName: decoded.lastName,
          email: decoded.email,
          role: decoded.role,
        });
        setIsAuthenticated(true);
        dispatch(
          setIsBackPaddockStaff(
            !!decoded.sub &&
              decoded.sub.toLowerCase().endsWith(BACK_PADDOCK_DOMAIN)
          )
        );
      } catch (error) {
        console.error('Error decoding token:', error);
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [isAuthenticated]);

  const logout = () => {
    Cookies.remove(ACCESS_TOKEN_STRING);
    Cookies.remove(REFRESH_TOKEN_STRING);
    setUser(null);
    setIsAuthenticated(false);
    dispatch(clearStaffState());
    dispatch(clearActiveOrganisation());
    queryClient.clear();
    navigate(CHECK_EMAIL_PATH);
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, setIsAuthenticated, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
